import { useGetAllViewRowsQuery } from 'store/services/flow/rows';
import { useGetFilterViewColumnsQuery, useGetViewColumnsQuery } from 'store/services/flow/viewColumns';
import { useGetUserFiltersQuery } from 'store/services/flow/userFilters';
import { useGetUserViewQuery } from 'store/services/flow/views';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FieldValues, SetValueConfig, UseFormWatch } from 'react-hook-form';
import { useFormContext, useWatch } from 'react-hook-form';

import { Box, Typography } from '@mui/material';
import { Tabs } from 'components/mui/common/Tabs';
import { TableControlsWrapper, TabsWrapper } from './TablesItem.styles';
import { Table } from 'components/common/Table/Table';
import { ActionModal } from 'components/mui/common/ActionModal';
import { FilterButton, SearchField } from 'components/common/Table/actions';
import { FeatureGate } from 'components/common/Posthog/FeatureGate.tsx';
import { SearchableBrowseItem } from 'components/common/SearchableBrowseItem/SearchableBrowseItem';
import { SelectedRowsWrapper, RowChip } from 'components/common/SearchableBrowseItem/styledComponents';
import { getEnumByColumnType } from 'components/common/Table/tableFunctions';
import type { TABLE_CELL_TYPE } from 'types/enums.ts';
import type { BasicFilter, CellsTypes, DataSchema, FilterColumnValue, ModalsType, Schema, TablesViewParam } from 'types';
import type { FormItemUiDefinition } from 'types/formItem.ts';
import { useTranslation } from 'react-i18next';

import { useSelector } from 'react-redux';
import { selectFilters } from 'store/entities/drawer/selectors.ts';
import { getTableParamsBySourceType, getTableViewParams } from 'utils/agendaRecord/agendaRecord.ts';
import { firstNonNull } from 'utils/firstNonNull';
import { isObjectArray } from 'utils/typeChecks';
import { isMobileApp, isNormalizedValueChanged } from 'utils/common';
import { useApplyDefaultFilters, useFilterCleanup, useFilterDrawerActions } from 'utils/hooks/userAgendaTable';
import { useColumnFilterDataSchema } from 'utils/hooks/userAgendaTable/useFilterDataSchema.ts';

interface ModalProps extends ModalsType {
  tableId: string;
  viewId: string;
  title: string;
  isMultiSelect: boolean;
  selectedRows: SelectedRow[];
  columnIdToShow: string;
  params?: TablesViewParam[];
  agendaId?: string;
  onConfirm: (e: SelectedRow[]) => void;
  watch: UseFormWatch<FieldValues>;
  initialSearchTerm: string;
  isAutoSelectSearchResultEnabled: boolean;
  onAutoSelectConsumed: () => void;
}

interface SelectedRow {
  id: string;
  value: string;
}

const TableModal = memo(
  ({
    open,
    onClose,
    title,
    onConfirm,
    tableId,
    viewId,
    isMultiSelect,
    selectedRows: selectedRowsProps = [],
    agendaId = '',
    watch,
    columnIdToShow,
    params = [],
    initialSearchTerm,
    isAutoSelectSearchResultEnabled,
    onAutoSelectConsumed,
  }: ModalProps) => {
    const { t } = useTranslation();
    const mobileApp = isMobileApp();
    const filterValues = useSelector(selectFilters(`table-listing-${viewId}`));
    const [selectedRows, setSelectedRows] = useState<SelectedRow[]>(selectedRowsProps);
    const [queryParams, setQueryParams] = useState<BasicFilter<DataSchema>>({
      page: 1,
      pageSize: 10,
      orderBy: '',
      orderDesc: false,
      search: initialSearchTerm,
    });

    const { data: { viewColumns: filterViewColumns = [] } = {} } = useGetFilterViewColumnsQuery(
      {
        viewId,
      },
      { skip: !viewId }
    );

    const { data: savedFilters } = useGetUserFiltersQuery({
      viewId,
      tableId,
      pageSize: 100,
      page: 1,
      orderDesc: false,
      search: '',
      orderBy: '' as never,
    });

    const filterDataSchema = useColumnFilterDataSchema({ viewColumns: filterViewColumns });

    const { applyDefaultFilters } = useApplyDefaultFilters({ savedFilters, viewId, isOverview: false, type: 'table' });

    const { handleFilterClick } = useFilterDrawerActions({
      filterDataSchema,
      agendaId,
      viewId,
      tableId,
      isOverview: false,
      savedFilters,
      type: 'table',
    });

    useFilterCleanup({
      isOverview: false,
      viewId,
      shouldPersistFilters: false,
    });

    useEffect(() => {
      applyDefaultFilters();
    }, [applyDefaultFilters]);

    useEffect(() => {
      if (!open) {
        return;
      }

      setSelectedRows(selectedRowsProps);
    }, [open, selectedRowsProps]);

    useEffect(() => {
      if (!open) {
        return;
      }

      setQueryParams((prev) => ({
        ...prev,
        page: 1,
        search: initialSearchTerm,
      }));
    }, [initialSearchTerm, open]);

    const { data: userViewsData } = useGetUserViewQuery(
      {
        parentId: tableId,
        viewId,
        viewParentType: 'Table',
      },
      {
        skip: !agendaId || !viewId,
      }
    );

    const { data: { viewColumns = [], viewName } = {} } = useGetViewColumnsQuery(
      {
        viewId,
      },
      { skip: !viewId }
    );

    const getParams = useMemo(() => getTableParamsBySourceType(viewColumns, watch, params), [viewColumns, watch, params]);

    const { data: tableDataRows, isFetching } = useGetAllViewRowsQuery({
      params: {
        tableId,
        viewId,
        value: '',
        ...queryParams,
      },
      body: {
        rowIds: [],
        valueColumns: getParams,
        filters: (filterValues ?? []).map((item) => ({
          columnId: item.formItemId,
          columnType: item.formItemType,
          values: Array.isArray(item.value) ? item.value : item.value ? [item.value] : [],
        })) as FilterColumnValue[],
      },
    });

    const dataSchema: Record<string, { label: string; type: TABLE_CELL_TYPE; translationKey: string }> = useMemo(
      () =>
        viewColumns
          .filter(({ isEnabled }) => isEnabled)
          .reduce<Record<string, { label: string; type: TABLE_CELL_TYPE; translationKey: string }>>((acc, item) => {
            const type = userViewsData?.viewParameters.find(({ parameterId }) => parameterId === item.columnId)?.columnType ?? 'Text';
            acc[item.columnId] = {
              label: item.columnName,
              type: getEnumByColumnType(type),
              translationKey: item.columnName,
            };
            return acc;
          }, {}),
      [userViewsData?.viewParameters, viewColumns]
    );

    const transformedData = useMemo(() => {
      if (!tableDataRows?.items) return null;

      return tableDataRows.items.map((row) => {
        return {
          id: row.rowId,
          identifier: row.rowId,
          ...row.cells.reduce((prev, curr) => {
            const column = userViewsData?.viewParameters.find(({ parameterId }) => parameterId === curr.columnId);

            if (!column || !column.columnType) return prev;

            const dataType = (column.columnType.slice(0, 1).toLowerCase() + column.columnType.slice(1)) as CellsTypes;

            return {
              ...prev,
              [column.parameterId]: curr[dataType]?.value,
            };
          }, {}),
        } as Schema<keyof typeof dataSchema, typeof dataSchema>;
      }) as Schema<keyof typeof dataSchema, typeof dataSchema>[];
    }, [userViewsData?.viewParameters, tableDataRows?.items]);

    const handleConfirm = useCallback(
      (rowsToConfirm = selectedRows) => {
        onConfirm(rowsToConfirm);
        onClose();
      },
      [onConfirm, onClose, selectedRows]
    );

    const handleSelectRows = useCallback(
      (rowIds: string[], rows?: Schema<string | number, DataSchema>[], shouldConfirm = false) => {
        setSelectedRows((prevState) => {
          const newItems =
            rows?.map((row) => ({
              id: row.id,
              value: row[columnIdToShow],
            })) ?? [];

          const selectedMap = new Map([...prevState, ...newItems].map((item) => [item.id, item]));
          const nextIds = Array.from(new Set(isMultiSelect ? rowIds : rowIds.slice(-1)));
          const nextSelectedRows = nextIds.map((id) => selectedMap.get(id)).filter((item): item is SelectedRow => Boolean(item));

          if (shouldConfirm) {
            handleConfirm(nextSelectedRows);
          }

          return nextSelectedRows;
        });
      },
      [columnIdToShow, handleConfirm, isMultiSelect]
    );

    useEffect(() => {
      if (!open || !isAutoSelectSearchResultEnabled || isFetching || !transformedData || transformedData.length !== 1) {
        return;
      }

      const firstRow = transformedData[0];
      onAutoSelectConsumed();
      const rowIdsToConfirm = isMultiSelect ? [...selectedRowsProps.map((row) => row.id), firstRow.id] : [firstRow.id];

      handleSelectRows(rowIdsToConfirm, [firstRow], true);
    }, [
      handleSelectRows,
      isAutoSelectSearchResultEnabled,
      isFetching,
      isMultiSelect,
      onAutoSelectConsumed,
      open,
      selectedRowsProps,
      transformedData,
    ]);

    return (
      <ActionModal
        open={open}
        title={title}
        onClose={onClose}
        onCancel={onClose}
        onSubmit={handleConfirm}
        maxWidth="lg"
        submitText={t('common.save')}>
        <TabsWrapper>
          <Tabs
            tabs={[{ id: viewId, label: viewName ?? '' }].map(({ id, label }) => ({
              id,
              label,
              path: '',
              content: (
                <Table
                  dataSchema={dataSchema}
                  data={transformedData ?? []}
                  isLoading={isFetching}
                  rows={tableDataRows?.totalCount}
                  controls={
                    <TableControlsWrapper isMobile={mobileApp}>
                      <SearchField
                        placeholder={t('common.search._')}
                        value={queryParams?.search}
                        onChange={(e) => {
                          setQueryParams((prev) => ({
                            ...prev,
                            search: e.target.value,
                          }));
                        }}
                        clearable
                      />
                      <FeatureGate flag="F20-2651">
                        <FilterButton onClick={handleFilterClick} activeCount={filterValues?.length} />
                      </FeatureGate>
                    </TableControlsWrapper>
                  }
                  onPageSizeChange={(pageSize) => {
                    setQueryParams?.((prev) => ({
                      ...prev,
                      pageSize,
                      page: 1,
                    }));
                  }}
                  onCurrentPageChange={(page) => {
                    setQueryParams?.((prev) => ({
                      ...prev,
                      page,
                    }));
                  }}
                  onSort={({ column, direction }) =>
                    setQueryParams?.((prev) => ({
                      ...prev,
                      orderBy: column,
                      orderDesc: direction === 'desc',
                    }))
                  }
                  sort={{
                    column: queryParams.orderBy as string,
                    direction: queryParams.orderDesc ? 'desc' : 'asc',
                  }}
                  onSelectRow={handleSelectRows}
                  isMultiSelect={isMultiSelect}
                  selectOnRowClick
                  defaultSelectedRows={selectedRows?.map((row) => (typeof row === 'object' ? row.id : row))}
                />
              ),
            }))}
            selectedTab={viewId}
            onTabClick={() => null}
          />
        </TabsWrapper>
      </ActionModal>
    );
  }
);

export interface TablesItemProps {
  agendaId?: string;
  onConfirm?: () => void;
  valueOptions?: SetValueConfig;
  formItemUiDefinition?: FormItemUiDefinition;
  lightError?: boolean;

  [key: string]: any;
}

export const TablesItem = memo(({ agendaId = '', onConfirm, valueOptions, ...formItem }: TablesItemProps) => {
  const { t } = useTranslation();
  const { setValue, watch, trigger: triggerForm, control, register } = useFormContext();
  const { viewId, tableId, columnIdToShow, params } = formItem.definition.tables;
  const formItemId = formItem.id ?? '';
  const tablesDefinition = formItem.definition?.tables;

  const isMultiSelect = tablesDefinition?.isMultiSelect ?? false;
  const [modalState, setModalState] = useState({
    open: false,
    searchTerm: '',
    isAutoSelectSearchResultEnabled: false,
  });

  const [selectedRows, ...watchedValue] = useWatch({
    control,
    name: [formItem.id, ...(params ?? []).map(({ valueFormItemId }: { valueFormItemId: string }) => valueFormItemId)],
  });
  const isInitialized = useRef(false);
  const oldWatchedValue = useRef(watchedValue);

  useEffect(() => {
    if (!formItemId) return;

    register(formItemId, formItem.registerProps);
    triggerForm(formItemId);
  }, [formItem.registerProps, formItemId, register, triggerForm]);

  useEffect(() => {
    if (!isInitialized.current) return;

    if (watchedValue.some((value) => value === undefined)) {
      return;
    }

    if (watchedValue.map((value, index) => isNormalizedValueChanged(value, oldWatchedValue.current[index])).some(Boolean)) {
      oldWatchedValue.current = watchedValue;
      setValue(formItem.id, [], {
        shouldDirty: false,
        shouldTouch: false,
      });
    }
  }, [watchedValue, formItem.id, setValue, selectedRows]);

  const { data: { viewColumns = [] } = {} } = useGetViewColumnsQuery(
    {
      viewId,
    },
    { skip: !viewId }
  );

  const { data: { items = [] } = {} } = useGetAllViewRowsQuery(
    {
      params: {
        tableId,
        viewId,
        value: '',
        page: 1,
        pageSize: 1000,
        orderBy: '',
        orderDesc: false,
        search: '',
      },
      body: {
        rowIds: isObjectArray(selectedRows ?? []) ? selectedRows.map(({ id }: { id: string }) => id).filter(Boolean) : selectedRows,
        valueColumns: getTableViewParams(viewColumns),
      },
    },
    { skip: !selectedRows || selectedRows?.length === 0, refetchOnMountOrArgChange: true }
  );

  const selectedValues = useMemo<SelectedRow[]>(() => {
    const selectedIds = Array.isArray(selectedRows) ? selectedRows.map((row) => (typeof row === 'string' ? row : row.id)) : [];

    return items
      .filter((item) => selectedIds.includes(item.rowId))
      .map((item) => {
        const cell = item.cells.find((itemCell) => itemCell.columnId === columnIdToShow);

        const value = firstNonNull<string>(
          cell?.text?.value,
          cell?.email?.value,
          cell?.number?.value != undefined ? String(cell.number.value) : undefined,
          cell?.phoneNumber?.value,
          cell?.reference?.value
        );

        return {
          id: item.rowId,
          value: value || '',
        };
      });
  }, [columnIdToShow, items, selectedRows]);

  useEffect(() => {
    if (!selectedRows?.length) return;

    const needsTransformation = selectedRows.every((row: SelectedRow) => typeof row === 'string');

    if (needsTransformation && selectedValues.length > 0) {
      isInitialized.current = true;
      oldWatchedValue.current = watchedValue;

      if (formItemId) {
        setValue(formItemId, selectedValues, {
          shouldDirty: false,
          shouldTouch: false,
        });
      }
    }
  }, [formItemId, selectedRows, selectedValues, setValue, watchedValue]);

  const isEditable = !formItem.detail && !formItem?.disabled && Boolean(formItemId) && Boolean(tablesDefinition);
  const canOpenModal = !formItem.detail && Boolean(formItemId) && Boolean(tablesDefinition);

  const toggleModal = useCallback(() => {
    setModalState((prev) => ({
      ...prev,
      open: !prev.open,
      isAutoSelectSearchResultEnabled: false,
    }));
  }, []);

  const handleCloseModal = useCallback(() => {
    setModalState((prev) => ({
      ...prev,
      open: false,
      isAutoSelectSearchResultEnabled: false,
    }));
  }, []);

  const handleOnClick = useCallback(() => {
    if (canOpenModal) {
      toggleModal();
    }
  }, [canOpenModal, toggleModal]);

  const handleSearchSubmit = useCallback((searchTerm: string, shouldAutoSelect: boolean) => {
    setModalState({
      open: true,
      searchTerm,
      isAutoSelectSearchResultEnabled: shouldAutoSelect,
    });
  }, []);

  const handleClear = useCallback(() => {
    if (formItemId) {
      setValue(formItemId, [], valueOptions);
      triggerForm(formItemId);
      onConfirm?.();
    }
  }, [formItemId, onConfirm, setValue, triggerForm, valueOptions]);

  const handleAutoSelectConsumed = useCallback(() => {
    setModalState((prev) => ({
      ...prev,
      isAutoSelectSearchResultEnabled: false,
    }));
  }, []);

  const handleModalConfirm = useCallback(
    (value: SelectedRow[]) => {
      if (formItemId) {
        setValue(formItemId, value, valueOptions);
        triggerForm(formItemId);
        onConfirm?.();
      }
    },
    [formItemId, onConfirm, setValue, triggerForm, valueOptions]
  );

  const renderField = useCallback(() => {
    if (!tablesDefinition) {
      return null;
    }

    if (!isMultiSelect) {
      const selectedValue = selectedValues[0]?.value ?? '';

      if (isEditable) {
        return (
          <SearchableBrowseItem
            name={selectedValue}
            onSearchSubmit={handleSearchSubmit}
            isSingleBrowse={true}
            error={Boolean(formItem.error)}
            lightError={Boolean(formItem.lightError)}
            onClear={handleClear}
          />
        );
      }

      if (!selectedValue) {
        return <Typography />;
      }

      return (
        <Typography onClick={canOpenModal ? handleOnClick : undefined} sx={{ cursor: canOpenModal ? 'pointer' : 'default' }}>
          {selectedValue}
        </Typography>
      );
    }

    return isEditable ? (
      <SearchableBrowseItem
        name=""
        onSearchSubmit={handleSearchSubmit}
        isSingleBrowse={false}
        error={Boolean(formItem.error)}
        lightError={Boolean(formItem.lightError)}
      />
    ) : null;
  }, [
    canOpenModal,
    formItem.error,
    formItem.lightError,
    handleClear,
    handleOnClick,
    handleSearchSubmit,
    isEditable,
    isMultiSelect,
    selectedValues,
    tablesDefinition,
  ]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      {isMultiSelect && selectedValues.length > 0 && (
        <SelectedRowsWrapper>
          {selectedValues.map((item) => (
            <RowChip
              key={item.id}
              label={<Typography component="span">{item.value}</Typography>}
              onClick={canOpenModal ? handleOnClick : undefined}
              clickable={canOpenModal}
            />
          ))}
        </SelectedRowsWrapper>
      )}

      <Box sx={{ flex: 1, minWidth: 0 }}>{renderField()}</Box>

      {formItem.error && !formItem.lightError && (
        <Typography className="field-validation-message" variant="body1" color="error">
          {formItem.error.message}
        </Typography>
      )}

      {modalState.open && tablesDefinition && (
        <TableModal
          open={true}
          watch={watch}
          title={t('flow.modulesAndAgendas.form.item.tables._')}
          agendaId={agendaId}
          selectedRows={selectedValues}
          initialSearchTerm={modalState.searchTerm}
          isAutoSelectSearchResultEnabled={modalState.isAutoSelectSearchResultEnabled}
          onAutoSelectConsumed={handleAutoSelectConsumed}
          {...tablesDefinition}
          onConfirm={handleModalConfirm}
          onClose={handleCloseModal}
        />
      )}
    </Box>
  );
});
